/**
 * Follow-list screen (route: FollowList, params: { username?, mode? }).
 *
 * MIGRATION (bare RN, P3-6): ported off Expo —
 *   expo-router        -> @react-navigation (useNavigation/route.params; the
 *                         header title is set via navigation.setOptions).
 *   @expo/vector-icons -> components/ui/Icon shim.
 * NB: the Expo screen read a `tab` query param; the frozen nav contract
 * (navigation/types.ts) names it `mode` ('followers' | 'following'). We read
 * `mode` and fall back to a legacy `tab` for safety.
 *
 * Services (api, socialGraph, moderation) + the LR4 block re-filter are verbatim.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  useWindowDimensions,
  Alert,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons } from '../components/ui/Icon';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale, shadowTokens,
} from '../constants/theme';
import { GlassPill, GlassPillGroup, GlassCard, GlassButton } from '../components/glass';
import Asset3D from '../components/ui/Asset3D';
import ParticleBackground from '../components/ui/ParticleBackground';
import { friendlyDisplayName } from '../lib/identity';
import { api } from '../services/api';
import { setFollow, amIFollowing, seedFollowState, subscribeCounts } from '../services/socialGraph';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../services/moderation';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'FollowList'>;
type Rt = RouteProp<RootStackParamList, 'FollowList'>;

const TABS = ['Followers', 'Following'] as const;
type TabKey = typeof TABS[number];

interface UserItem {
  id: string;
  username: string;
  display_name: string;
  avatar_url?: string;
  bio?: string;
  is_following?: boolean;
}

// Map a (camelCase) backend user row to the client UserItem shape. Backend
// follower/following list routes don't exist yet, but normalize defensively so
// the screen works the moment they do.
function normalizeUser(raw: any): UserItem {
  return {
    id: raw?.id,
    username: raw?.username ?? 'user',
    display_name: raw?.displayName ?? raw?.display_name ?? '',
    avatar_url: raw?.avatar ?? raw?.avatar_url,
    bio: raw?.bio,
    is_following: raw?.isFollowing ?? raw?.is_following ?? false,
  };
}

export default function FollowListScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<Rt>();
  const username = route.params?.username ?? '';
  // Frozen contract uses `mode`; tolerate a legacy `tab` param too.
  const mode = route.params?.mode ?? (route.params as any)?.tab;
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  const [activeTab, setActiveTab] = useState<TabKey>(
    mode === 'following' ? 'Following' : 'Followers'
  );
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);

  // Header title = the handle whose connections we're viewing.
  useEffect(() => {
    navigation.setOptions({ title: username ? `@${username}` : 'Connections' });
  }, [navigation, username]);

  const fetchUsers = useCallback(async () => {
    setError(false);
    // GET /users/:username/followers and /:username/following return
    // { data: { users: [...], hasMore, nextCursor } } (api() unwraps one level,
    // so res.data === { users, ... }). Each user carries isFollowing for the viewer.
    const endpoint =
      activeTab === 'Followers'
        ? `/users/${encodeURIComponent(username)}/followers`
        : `/users/${encodeURIComponent(username)}/following`;
    try {
      // Read defensively across possible envelope keys; api() unwraps one level.
      const res = await api<any>(endpoint);
      if (res.success && res.data) {
        const list = Array.isArray(res.data)
          ? res.data
          : res.data.users ?? res.data.items ?? [];
        const mapped = (list as any[]).map(normalizeUser);
        // Seed the shared social-graph store from the server's per-row isFollowing
        // so this screen — and every other follow button — share one source of
        // truth. Rows then RENDER from amIFollowing (below), not the frozen
        // is_following snapshot, so a toggle on any screen reflects here too.
        for (const u of mapped) {
          seedFollowState({ id: u.id, username: u.username }, !!u.is_following);
        }
        setUsers(mapped);
      } else if (!res.success) {
        setUsers([]);
        setError(true);
      }
    } catch {
      setUsers([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [activeTab, username]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchUsers();
    setRefreshing(false);
  }, [fetchUsers]);

  useEffect(() => {
    setLoading(true);
    fetchUsers();
  }, [fetchUsers]);

  // LR4: live re-filter when the block set changes (e.g. user blocks someone in
  // this list, or a block:update socket event arrives) so blocked users vanish
  // from the list immediately without a refetch.
  const [blockTick, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);
  const visibleUsers = (Array.isArray(users) ? users : []).filter(
    (u) => !(isBlocked(u?.id, u?.username) || isMuted(u?.id, u?.username))
  );
  // blockTick is referenced so the filter recomputes on block changes.
  void blockTick;

  // Live follow state: subscribe to the shared graph store for every row so a
  // follow/unfollow performed on ANOTHER screen (or arriving via the
  // follow:update socket) re-renders this list. subscribeCounts fires on every
  // authoritative count change for that user; we bump a tick to re-read
  // amIFollowing at render time (single source of truth, no local-only drift).
  const [graphTick, setGraphTick] = useState(0);
  useEffect(() => {
    const unsubs = users.map((u) =>
      subscribeCounts({ id: u.id, username: u.username }, () =>
        setGraphTick((n) => n + 1)
      )
    );
    return () => unsubs.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
  }, [users]);
  // graphTick is referenced so following state recomputes on cross-screen changes.
  void graphTick;

  async function handleToggleFollow(user: UserItem) {
    // Read the CURRENT truth from the shared store (not the frozen row snapshot)
    // so we never toggle off a stale is_following after a cross-screen change.
    const wasFollowing = amIFollowing({ id: user.id, username: user.username });
    // Optimistic flip: seed the shared store immediately so this row (and every
    // other follow button on any screen) reflect it, then re-render.
    seedFollowState({ id: user.id, username: user.username }, !wasFollowing);
    setUsers((prev) =>
      prev.map((u) =>
        u.id === user.id ? { ...u, is_following: !wasFollowing } : u
      )
    );
    // LR4: route through the social-graph service so counts reconcile, the
    // followed user's posts propagate to the feed, and the follow:update socket
    // event syncs other devices.
    const res = await setFollow({ id: user.id, username: user.username }, !wasFollowing);
    if (!res.success) {
      // Roll back BOTH the shared store and the row, then surface the real error
      // instead of failing silently — esp. "Already following" from the server.
      seedFollowState({ id: user.id, username: user.username }, wasFollowing);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, is_following: wasFollowing } : u
        )
      );
      Alert.alert(
        wasFollowing ? "Couldn't unfollow" : "Couldn't follow",
        res.error || 'Something went wrong. Please try again.'
      );
    }
  }

  function renderUserRow({ item }: { item: UserItem }) {
    // Triage P0 identity: present a friendly name, never the auto-generated
    // `d967bbgzv2` handle as the display line (the @handle below still shows).
    const shownName = friendlyDisplayName(item.display_name, item.username);
    // Follow state is read LIVE from the shared graph store (seeded from the
    // server on load + kept in sync by subscribeCounts/graphTick) — never the
    // local-only is_following snapshot — so it stays correct across screens.
    const following = amIFollowing({ id: item.id, username: item.username });
    return (
      <View style={styles.rowWrap}>
        <GlassCard
          padding={spacing.md}
          radius={radius.lg}
          contentStyle={styles.userRow}
          onPress={() => navigation.navigate('UserProfile', { username: item.username })}
          accessibilityLabel={`View ${shownName}'s profile`}
        >
          {item.avatar_url ? (
            <CachedImage source={{ uri: item.avatar_url }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, styles.avatarFallback]}>
              <Text style={styles.avatarLetter}>
                {shownName.charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <View style={styles.userInfo}>
            <Text style={styles.userName} numberOfLines={1}>
              {shownName}
            </Text>
            <Text style={styles.userHandle} numberOfLines={1}>@{item.username || 'user'}</Text>
          </View>
          <TouchableOpacity
            style={[styles.followBtn, following && styles.followingBtn]}
            onPress={() => handleToggleFollow(item)}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={following ? `Unfollow ${item.username}` : `Follow ${item.username}`}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text
              style={[styles.followBtnText, following && styles.followingBtnText]}
            >
              {following ? 'Following' : 'Follow'}
            </Text>
          </TouchableOpacity>
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Living animated background (design-system): deep premium glass surface. */}
      <ParticleBackground />

      {/* Segmented tabs — single GlassPill implementation (no ad-hoc pill styles). */}
      <View style={containerStyle}>
        <GlassPillGroup style={styles.tabRow}>
          {TABS.map((t) => (
            <GlassPill
              key={t}
              label={t}
              active={activeTab === t}
              onPress={() => setActiveTab(t)}
              accessibilityLabel={t}
            />
          ))}
        </GlassPillGroup>
      </View>

      {loading ? (
        <View style={[styles.listContent, containerStyle]}>
          {[0, 1, 2, 3, 4].map((i) => (
            <GlassCard key={i} style={styles.rowWrap} padding={spacing.md} radius={radius.lg}>
              <View style={styles.skeletonRow}>
                <View style={styles.skeletonAvatar} />
                <View style={styles.skeletonLines}>
                  <View style={[styles.skeletonLine, { width: '50%' }]} />
                  <View style={[styles.skeletonLine, styles.skeletonLineThin, { width: '32%' }]} />
                </View>
              </View>
            </GlassCard>
          ))}
        </View>
      ) : (
        <FlatList
          data={visibleUsers}
          extraData={graphTick}
          keyExtractor={(item, index) => item?.id ?? String(index)}
          renderItem={renderUserRow}
          initialNumToRender={15}
          maxToRenderPerBatch={12}
          windowSize={11}
          removeClippedSubviews
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
                <Asset3D name={error ? 'error' : 'community'} size={120} style={{ marginBottom: spacing.md }} />
                <Text style={styles.emptyText}>
                  {error ? `Couldn't load ${activeTab.toLowerCase()}` : `No ${activeTab.toLowerCase()} yet`}
                </Text>
                <Text style={styles.emptySubtext}>
                  {error
                    ? 'Check your connection and try again.'
                    : activeTab === 'Followers'
                      ? 'When people follow, they will appear here.'
                      : 'Follows will show up here once you start connecting.'}
                </Text>
                {error ? (
                  <GlassButton
                    title="Retry"
                    icon="refresh-outline"
                    onPress={onRefresh}
                    fullWidth
                    style={styles.emptyCta}
                  />
                ) : null}
              </GlassCard>
            </View>
          }
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, containerStyle]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  tabRow: {
    paddingHorizontal: layoutTokens.gutter,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  // Row = shared GlassCard; this only separates rows in the list.
  rowWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.sm,
  },
  userRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: radius.full,
  },
  avatarFallback: {
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    ...typographyScale.h3,
    fontSize: 17,
    color: colors.primary,
  },
  userInfo: {
    flex: 1,
    marginLeft: spacing.md,
  },
  userName: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
  },
  userHandle: {
    ...typographyScale.caption,
    fontSize: 13,
    color: colors.muted,
    marginTop: 1,
  },
  followBtn: {
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    ...shadowTokens.bar,
    shadowColor: colors.primary,
  },
  followingBtn: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.primary,
    shadowOpacity: 0,
    elevation: 0,
  },
  followBtnText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    fontSize: 13,
    color: colors.white,
  },
  followingBtnText: {
    color: colors.primary,
  },
  listContent: {
    paddingTop: spacing.xs,
    // Clear the floating glass tab bar so the last row is visible.
    paddingBottom: layoutTokens.bottomNavInset,
    flexGrow: 1,
  },
  // --- Loading skeleton ---
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  skeletonAvatar: {
    width: 44,
    height: 44,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '14',
  },
  skeletonLines: {
    flex: 1,
    gap: spacing.sm,
  },
  skeletonLine: {
    height: 12,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '14',
  },
  skeletonLineThin: {
    height: 10,
    backgroundColor: colors.primary + '0D',
  },
  // --- Empty / error states ---
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: spacing.xxxl + spacing.xxl,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyCta: {
    marginTop: spacing.lg,
  },
});
